import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ArrowRight, Music4, Sparkles, GraduationCap, Building2, Mic2, BookOpen, Maximize2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Reveal } from '../hooks/useReveal';
import { assets, culturalPortfolio } from '../mock';
import { Dialog, DialogContent } from '../components/ui/dialog';

const eduIcons = [GraduationCap, Mic2, BookOpen];

const CulturalPage = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const c = culturalPortfolio[lang];
  const [video, setVideo] = useState(null);
  const [poster, setPoster] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="pt-24">
      {/* Banner */}
      <section className="relative h-[52vh] min-h-[360px] flex items-center justify-center overflow-hidden">
        <img src={assets.culturalImage} alt="Cultural" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14120d]/45 via-[#14120d]/35 to-[#14120d]" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 45%, rgba(10,9,6,0.55), rgba(10,9,6,0) 60%)' }} />
        <div className="relative z-10 text-center px-5 max-w-3xl">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#e6b83f]/70" />
            <span className="font-sans-el text-[12px] tracking-[0.35em] uppercase text-[#f5deA0]">2026 · 2027</span>
            <span className="h-px w-8 bg-[#e6b83f]/70" />
          </div>
          <h1 className="font-deco text-4xl sm:text-6xl font-bold gold-gradient-text">{c.bannerTitle}</h1>
          <p className="font-serif-el italic text-lg sm:text-2xl text-[#dbd4c4] mt-5">{c.bannerSubtitle}</p>
        </div>
      </section>

      {/* Mission */}
      <section className="section-pad bg-[#14120d]">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h2 className="font-deco text-3xl sm:text-4xl font-semibold text-[#f7f1e3]">{c.missionTitle}</h2>
            <div className="gold-line w-24 mx-auto my-6" />
            <p className="font-serif-el text-xl text-[#cbc4b3] leading-relaxed">{c.mission}</p>
          </Reveal>
          <Reveal delay={80}>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
              {c.features.map((f) => (
                <span key={f} className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#e6b83f]/25 rounded-sm font-sans-el text-[14px] text-[#dbd4c4]">
                  <Sparkles size={14} className="text-[#e6b83f]" /> {f}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Director */}
      <section className="section-pad bg-gradient-to-b from-[#14120d] via-[#1e1a11] to-[#14120d]">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="card-lux p-8 sm:p-12">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-[#e6b83f]/60" />
                <span className="font-sans-el text-[12px] tracking-[0.3em] uppercase text-[#e6b83f]">{c.directorEyebrow}</span>
              </div>
              <div className="grid lg:grid-cols-[auto_1fr] gap-8 items-start">
                <div className="w-28 h-36 rounded-sm overflow-hidden border border-[#e6b83f]/40 shrink-0">
                  <img src={assets.directorImage} alt={c.directorName} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <h3 className="font-deco text-3xl text-[#f7f1e3]">{c.directorName}</h3>
                  <p className="font-sans-el text-[14px] tracking-wide text-[#e6b83f] mt-1">{c.directorRole}</p>
                  <p className="font-serif-el text-lg text-[#cbc4b3] mt-5 leading-relaxed">{c.directorBio}</p>
                  <p className="font-sans-el text-[12px] tracking-[0.2em] uppercase text-[#a19a88] mt-7 mb-3">{c.collabTitle}</p>
                  <div className="flex flex-wrap gap-2.5">
                    {c.collaborations.map((x) => (
                      <span key={x} className="px-4 py-2 rounded-sm bg-[#e6b83f]/8 border border-[#e6b83f]/20 font-sans-el text-[13px] text-[#dbd4c4]">{x}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Portfolio */}
      <section className="section-pad bg-[#14120d]">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#e6b83f]/60" />
                <span className="font-sans-el text-[12px] tracking-[0.3em] uppercase text-[#e6b83f]">Portafolio</span>
                <span className="h-px w-8 bg-[#e6b83f]/60" />
              </div>
              <h2 className="font-deco text-3xl sm:text-4xl font-semibold text-[#f7f1e3]">{c.portfolioTitle}</h2>
              <p className="font-serif-el italic text-lg text-[#cdc6b5] mt-4">{c.portfolioSubtitle}</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {c.shows.map((s, i) => (
              <Reveal key={s.n} delay={(i % 3) * 70}>
                <div className="card-lux h-full flex flex-col overflow-hidden">
                  {s.poster ? (
                    <button onClick={() => (s.video ? setVideo(s.video) : setPoster(s.poster))} className="group relative w-full aspect-video overflow-hidden bg-[#0f0d09]">
                      <img
                        src={s.poster}
                        alt={s.title} loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full bg-[#e6b83f]/90 flex items-center justify-center text-[#14120d] group-hover:scale-110 transition-transform">
                          {s.video ? <Play size={22} fill="currentColor" /> : <Maximize2 size={20} />}
                        </span>
                      </span>
                    </button>
                  ) : s.video ? (
                    <button onClick={() => setVideo(s.video)} className="group relative w-full aspect-video overflow-hidden">
                      <img
                        src={`https://i.ytimg.com/vi/${s.video}/sddefault.jpg`}
                        onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${s.video}/hqdefault.jpg`; }}
                        alt={s.title} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute inset-0 bg-black/30 group-hover:bg-black/15 transition-colors" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full bg-[#e6b83f]/90 flex items-center justify-center text-[#14120d] group-hover:scale-110 transition-transform">
                          <Play size={22} fill="currentColor" />
                        </span>
                      </span>
                    </button>
                  ) : (
                    <div className="w-full aspect-video flex items-center justify-center bg-gradient-to-br from-[#221d13] to-[#14120d] border-b border-[#e6b83f]/15">
                      <span className="font-deco text-5xl gold-gradient-text font-bold opacity-70">{s.n}</span>
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-baseline gap-2">
                      <span className="font-sans-el text-[12px] tracking-[0.2em] text-[#e6b83f]">{s.n}</span>
                      <h3 className="font-deco text-xl text-[#f7f1e3]">{s.title}</h3>
                    </div>
                    <p className="font-sans-el text-[15px] text-[#c2bbaa] font-light mt-3 leading-relaxed flex-1">{s.desc}</p>
                    {s.video && (
                      <button onClick={() => setVideo(s.video)} className="inline-flex items-center gap-2 mt-4 font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#f5deA0] hover:text-[#f7ecc9] transition-colors">
                        <Play size={13} fill="currentColor" /> {c.watch}
                      </button>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* New album */}
      <section className="section-pad bg-[#14120d]">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="card-lux overflow-hidden grid md:grid-cols-2">
              <div className="aspect-square md:aspect-auto">
                <img src={assets.albumImage} alt={c.albumName} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 sm:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-px w-8 bg-[#e6b83f]/60" />
                  <span className="font-sans-el text-[12px] tracking-[0.3em] uppercase text-[#e6b83f]">{c.albumEyebrow}</span>
                </div>
                <h3 className="font-deco text-3xl sm:text-4xl gold-gradient-text font-bold">{c.albumName}</h3>
                <p className="font-serif-el text-lg text-[#cbc4b3] mt-5 leading-relaxed">{c.albumDesc}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Education */}
      <section className="section-pad bg-gradient-to-b from-[#14120d] via-[#1e1a11] to-[#14120d]">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="font-deco text-3xl sm:text-4xl font-semibold text-[#f7f1e3] text-center">{c.eduTitle}</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {c.edu.map((e, i) => {
              const Icon = eduIcons[i] || GraduationCap;
              return (
                <Reveal key={e.t} delay={i * 90}>
                  <div className="card-lux h-full p-8">
                    <div className="w-14 h-14 rounded-sm border border-[#e6b83f]/40 flex items-center justify-center mb-5">
                      <Icon size={24} strokeWidth={1.4} className="text-[#f5deA0]" />
                    </div>
                    <h3 className="font-deco text-xl text-[#f7f1e3]">{e.t}</h3>
                    <p className="font-sans-el text-[15px] text-[#c2bbaa] font-light mt-3 leading-relaxed">{e.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="section-pad bg-[#14120d]">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <h2 className="font-deco text-2xl sm:text-3xl font-semibold text-[#f7f1e3]">{c.clientsTitle}</h2>
            <div className="gold-line w-20 mx-auto my-6" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
              {c.clients.map((cl) => (
                <div key={cl} className="flex items-center gap-3 justify-center sm:justify-start px-5 py-4 card-lux rounded-sm">
                  <Building2 size={18} className="text-[#e6b83f] shrink-0" />
                  <span className="font-sans-el text-[15px] text-[#dbd4c4] text-left">{cl}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={80}>
            <button onClick={() => navigate('/#contact')} className="btn-gold inline-flex items-center gap-2 mt-12 px-8 py-4 rounded-sm text-[13px] tracking-[0.2em] uppercase font-medium">
              {c.cta} <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>

      <Dialog open={!!video} onOpenChange={(o) => !o && setVideo(null)}>
        <DialogContent className="max-w-4xl bg-transparent border-0 p-0 shadow-none">
          {video && (
            <div className="aspect-video w-full">
              <iframe className="w-full h-full rounded-sm" src={`https://www.youtube.com/embed/${video}?autoplay=1`} title="LAJO" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!poster} onOpenChange={(o) => !o && setPoster(null)}>
        <DialogContent className="max-w-lg bg-transparent border-0 p-0 shadow-none">
          {poster && <img src={poster} alt="LAJO" className="w-full h-auto rounded-sm" />}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default CulturalPage;
